import { motion } from 'framer-motion';
import { 
  HelpCircle, 
  ChevronDown, 
  ChevronUp, 
  Search,
  MessageCircle,
  Mail
} from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FAQContentProps {
  onNavigate?: (view: 'home' | 'login' | 'signup' | 'forgot-password' | 'reset-password' | 'privacy-policy' | 'cookie-policy' | 'terms-of-service' | 'gdpr-compliance' | 'refund-cancellation' | 'faq' | 'about-us' | 'contact-us') => void;
}

export const FAQ_ITEM_IDS = ['faq0', 'faq1', 'faq2', 'faq3', 'faq4', 'faq5', 'faq6', 'faq7', 'faq8', 'faq9', 'faq10', 'faq11'] as const;

/** @deprecated Use buildFaqPageJsonLd from content/faq */
export { buildFaqPageJsonLd } from '../../content/faq';

const FAQContent: React.FC<FAQContentProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [activeAccordion, setActiveAccordion] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const toggleAccordion = (id: string) => {
    setActiveAccordion(prev => prev === id ? null : id);
  };

  const faqIds = FAQ_ITEM_IDS;
  const categoryKeys = ['views.faq.categoryCredits', 'views.faq.categoryOptimization', 'views.faq.categoryFeatures', 'views.faq.categoryFeatures', 'views.faq.categoryCredits', 'views.faq.categoryTechnical', 'views.faq.categorySecurity', 'views.faq.categoryEnterprise', 'views.faq.categoryTechnical', 'views.faq.categoryOptimization', 'views.faq.categoryCredits', 'views.faq.categoryOptimization'] as const;
  const faqs = faqIds.map((key, i) => ({
    id: `faq-${i}`,
    questionKey: `views.faq.items.${key}.question`,
    answerKey: `views.faq.items.${key}.answer`,
    categoryKey: categoryKeys[i]
  }));

  const categories = ['Credits', 'Optimization', 'Features', 'Technical', 'Security', 'Enterprise'];
  const categoryLabelKey: Record<string, string> = {
    Credits: 'views.faq.categoryCredits',
    Optimization: 'views.faq.categoryOptimization',
    Features: 'views.faq.categoryFeatures',
    Technical: 'views.faq.categoryTechnical',
    Security: 'views.faq.categorySecurity',
    Enterprise: 'views.faq.categoryEnterprise'
  };

  const filteredFAQs = faqs.filter(faq => {
    if (!searchTerm.trim()) return true;
    const q = t(faq.questionKey).toLowerCase();
    const a = t(faq.answerKey).toLowerCase();
    const c = t(faq.categoryKey).toLowerCase();
    const term = searchTerm.toLowerCase();
    return q.includes(term) || a.includes(term) || c.includes(term);
  });

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-[var(--vscode-primary)]/20 rounded-full flex items-center justify-center">
            <HelpCircle className="w-8 h-8 text-[var(--vscode-primary)]" />
          </div>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold vscode-text-primary mb-4">{t('views.faq.title')}</h1>
        <p className="text-xl vscode-text-secondary max-w-3xl mx-auto">
          {t('views.faq.subtitle')}
        </p>
      </div>

      {/* Search Bar */}
      <div className="max-w-2xl mx-auto mb-8">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 vscode-text-tertiary" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 vscode-bg-elevated vscode-border rounded-lg vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none focus:ring-2 focus:ring-[var(--vscode-border-focus)] border"
            placeholder={t('views.faq.searchPlaceholder')}
          />
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        <button
          onClick={() => setSearchTerm('')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
            searchTerm === ''
              ? 'bg-[var(--vscode-primary)] text-[var(--vscode-primary-text)] border-[var(--vscode-primary)]'
              : 'vscode-bg-elevated vscode-text-secondary hover:vscode-bg-hover vscode-border'
          }`}
        >
          {t('views.faq.allCategories')}
        </button>
        {categories.map(category => {
          const label = t(categoryLabelKey[category]);
          return (
            <button
              key={category}
              onClick={() => setSearchTerm(label)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                searchTerm === label
                  ? 'bg-[var(--vscode-primary)] text-[var(--vscode-primary-text)] border-[var(--vscode-primary)]'
                  : 'vscode-bg-elevated vscode-text-secondary hover:vscode-bg-hover vscode-border'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* FAQ List */}
      <div className="max-w-4xl mx-auto space-y-4">
        {filteredFAQs.length > 0 ? (
          filteredFAQs.map((faq) => (
            <motion.div 
              key={faq.id} 
              className="vscode-bg-elevated rounded-xl border vscode-border overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <button
                onClick={() => toggleAccordion(faq.id)}
                className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[var(--vscode-border-focus)] focus:ring-inset"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <span className="text-xs bg-[var(--vscode-primary)]/20 text-[var(--vscode-primary)] px-2 py-1 rounded-full border border-[var(--vscode-primary)]/30">
                      {t(faq.categoryKey)}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold vscode-text-primary mt-2">{t(faq.questionKey)}</h3>
                </div>
                {activeAccordion === faq.id ? (
                  <ChevronUp className="w-5 h-5 text-[var(--vscode-primary)] flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 vscode-text-tertiary flex-shrink-0" />
                )}
              </button>
              
              {activeAccordion === faq.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-4 border-t vscode-border">
                    <p className="vscode-text-secondary mt-4 leading-relaxed">{t(faq.answerKey)}</p>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))
        ) : (
          <div className="text-center py-12">
            <HelpCircle className="w-16 h-16 vscode-text-tertiary mx-auto mb-4" />
            <h3 className="text-xl font-semibold vscode-text-primary mb-2">{t('views.faq.noResults')}</h3>
            <p className="vscode-text-tertiary">{t('views.faq.noResultsHint')}</p>
          </div>
        )}
      </div>

      {/* Contact Section */}
      <div className="max-w-2xl mx-auto mt-16 vscode-bg-elevated rounded-xl p-6 border vscode-border">
        <div className="text-center">
          <MessageCircle className="w-12 h-12 text-[var(--vscode-primary)] mx-auto mb-4" />
          <h3 className="text-xl font-semibold vscode-text-primary mb-2">{t('views.faq.stillHaveQuestions')}</h3>
          <p className="vscode-text-secondary mb-6">
            {t('views.faq.cantFind')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => {
                if (onNavigate) {
                  onNavigate('contact-us');
                } else {
                  window.location.href = '/contact-us';
                }
              }}
              className="inline-flex items-center justify-center px-6 py-3 bg-[var(--vscode-primary)] hover:opacity-90 text-[var(--vscode-primary-text)] font-semibold rounded-lg transition-colors"
            >
              <Mail className="w-5 h-5 mr-2" />
              {t('views.faq.contactSupport')}
            </button>
            <a
              href="/login"
              className="inline-flex items-center justify-center px-6 py-3 border vscode-border vscode-text-secondary font-semibold rounded-lg hover:vscode-bg-hover transition-colors"
            >
              {t('views.faq.signInForHelp')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQContent;
